import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Eye, Trash2, Plus, Filter, Phone, CheckCircle, FolderOpen, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import usePersistedSearchParams from '../hooks/usePersistedSearchParams';
import DataTable from '../components/DataTable';
import ConfirmDialog from '../components/ConfirmDialog';
import LeadModal from '../components/LeadModal';
import ClientDataViewerModal from '../components/leads/ClientDataViewerModal';
import ImportLeadsModal from '../components/ImportLeadsModal';
import IconButton from '../components/common/IconButton';
import SmartSearchBar from '../components/leads/SmartSearchBar';
import StatusFilterTabs from '../components/leads/StatusFilterTabs';
import RoleIndicatorChip from '../components/leads/RoleIndicatorChip';
import FilterDrawer from '../components/leads/FilterDrawer';
import StatusCellWithTooltip from '../components/clients/StatusCellWithTooltip';
import { contractApi } from '../services/contractService';
import {
  LeadDto,
  LeadFilterDto,
  leadsApi,
  handleApiError,
  formatDate,
  BulkDeleteDto,
  getBenefitTypeName
} from '../services/leadsService';
import {
  statusesApi,
  leadSourcesApi,
  departmentsApi,
  StatusDto,
  LeadSourceDto,
  LeadSourceType,
  DepartmentDto
} from '../services/settingsService';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FilterContainer = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: var(--shadow-light);
`;

const FilterRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AdvancedFilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: var(--hover-background);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const BulkActions = styled.div`
  background: white;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  padding: 12px 20px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const BulkInfo = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const BulkButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 767px) {
    flex-direction: column;
    width: 100%;
  }
`;

const BulkSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  background: white;
  min-width: 160px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const BulkButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 8px 14px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : props.variant === 'danger' ? `
    background: transparent;
    color: var(--secondary-color);
    border: 1px solid var(--secondary-color);
    &:hover:not(:disabled) {
      color: var(--error-color);
      border-color: var(--error-color);
    }
  ` : `
    background: white;
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusText = styled.span<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.color};
  filter: saturate(0.7) brightness(0.85);
`;

const ActionsCell = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

// Hit area around the row checkbox; stops the click from opening the lead for editing
const SelectCell = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  cursor: pointer;
`;

const ErrorMessage = styled.div`
  background: var(--error-light);
  color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--error-color);
`;

const LeadsPage: React.FC = () => {
  const { isManager, isStaff } = useAuth();
  const { initialParams, persistSearchParams } = usePersistedSearchParams('leads');
  const navigate = useNavigate();

  const [leads, setLeads] = useState<LeadDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [statuses, setStatuses] = useState<StatusDto[]>([]);
  const [leadSources, setLeadSources] = useState<LeadSourceDto[]>([]);
  const [departments, setDepartments] = useState<DepartmentDto[]>([]);

  // Pagination - read from URL (or the remembered list view)
  const [currentPage, setCurrentPage] = useState(() => {
    const page = initialParams.get('page');
    return page ? parseInt(page) : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const size = initialParams.get('pageSize');
    return size ? parseInt(size) : 25;
  });
  const [totalCount, setTotalCount] = useState(0);

  // Filters - read from URL (or the remembered list view)
  const [searchTerm, setSearchTerm] = useState(() => initialParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState<string[]>(() => {
    const statuses = initialParams.get('statuses');
    return statuses ? statuses.split(',') : [];
  });
  const [departmentIds, setDepartmentIds] = useState<number[]>(() => {
    const d = initialParams.get('department');
    return d ? d.split(',').map(x => parseInt(x)).filter(x => !isNaN(x)) : [];
  });
  // P8b: פילטר סוכן/ארגון (יחיד; '' = הכל, '-1' = ללא)
  const [agentFilter, setAgentFilter] = useState<string>(() => initialParams.get('agent') || '');
  const [organizationFilter, setOrganizationFilter] = useState<string>(() => initialParams.get('organization') || '');
  const [dateFromFilter, setDateFromFilter] = useState(() => initialParams.get('dateFrom') || '');
  const [dateToFilter, setDateToFilter] = useState(() => initialParams.get('dateTo') || '');

  // Update URL and remember the list view when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', currentPage.toString());
    if (pageSize !== 25) params.set('pageSize', pageSize.toString());
    if (searchTerm) params.set('search', searchTerm);
    if (statusFilter.length > 0) params.set('statuses', statusFilter.join(','));
    if (departmentIds.length > 0) params.set('department', departmentIds.join(','));
    if (agentFilter) params.set('agent', agentFilter);
    if (organizationFilter) params.set('organization', organizationFilter);
    if (dateFromFilter) params.set('dateFrom', dateFromFilter);
    if (dateToFilter) params.set('dateTo', dateToFilter);

    persistSearchParams(params);
  }, [currentPage, pageSize, searchTerm, statusFilter, departmentIds, agentFilter, organizationFilter, dateFromFilter, dateToFilter, persistSearchParams]);

  // Bulk actions
  const [bulkAssignDepartment, setBulkAssignDepartment] = useState<string>('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Single lead delete
  const [deleteConfirmLead, setDeleteConfirmLead] = useState<{ id: number; name: string; hasContract: boolean } | null>(null);

  // Modals
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [clientDataLead, setClientDataLead] = useState<LeadDto | null>(null);

  // Contract status cache for leads
  const [contractStatuses, setContractStatuses] = useState<Record<number, string>>({});

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filterParams: LeadFilterDto = {
        page: currentPage,
        pageSize: pageSize,
        searchTerm: searchTerm || undefined,
        statusIds: statusFilter.length > 0 ? statusFilter.map(id => parseInt(id)) : undefined,
        // multi-select; -1 = "ללא מחלקה" (מטופל בשרת). פילטר בלבד — לא מגביל נראות
        departmentIds: departmentIds.length > 0 ? departmentIds : undefined,
        // P8b: פילטר סוכן/ארגון יחיד (-1 = ללא)
        agentSourceId: agentFilter ? parseInt(agentFilter) : undefined,
        organizationSourceId: organizationFilter ? parseInt(organizationFilter) : undefined,
        dateFrom: dateFromFilter || undefined,
        dateTo: dateToFilter || undefined,
      };

      const result = await leadsApi.getAll(filterParams);
      setLeads(result.leads);
      setTotalCount(result.totalCount);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, statusFilter, departmentIds, agentFilter, organizationFilter, dateFromFilter, dateToFilter]);

  // Load contract statuses for current leads
  const loadContractStatuses = useCallback(async (leadIds: number[]) => {
    try {
      const contractPromises = leadIds.map(async (leadId) => {
        try {
          const contract = await contractApi.getByLeadId(leadId);
          return contract ? { leadId, status: contract.status } : { leadId, status: null };
        } catch {
          return { leadId, status: null };
        }
      });

      const results = await Promise.all(contractPromises);
      const statusMap: Record<number, string> = {};

      results.forEach(({ leadId, status }) => {
        if (status) {
          statusMap[leadId] = status;
        }
      });

      setContractStatuses(statusMap);
    } catch (error) {
      console.error('Error loading contract statuses:', error);
    }
  }, []);

  const loadInitialData = useCallback(async () => {
    try {
      const [statusesData, leadSourcesData, departmentsData] = await Promise.all([
        statusesApi.search('', 1), // Only load lead statuses (group 1)
        leadSourcesApi.getAll(),
        departmentsApi.getAll() // סינון לפי מחלקה זמין לכל הצוות (מציג גם מחלקות מושבתות לאיתור תיקים)
      ]);

      setStatuses(statusesData);
      setLeadSources(leadSourcesData);
      setDepartments(departmentsData);
    } catch (err) {
      setError(handleApiError(err));
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  // Load contract statuses when leads change
  useEffect(() => {
    if (leads.length > 0) {
      const leadIds = leads.map(lead => lead.id);
      loadContractStatuses(leadIds);
    }
  }, [leads, loadContractStatuses]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter([]);
    setDepartmentIds([]);
    setAgentFilter('');
    setOrganizationFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setCurrentPage(1);
  };

  const handleAdvancedFilter = (filters: {
    departmentIds: number[];
    agentFilter: string;
    organizationFilter: string;
    dateFrom: string;
    dateTo: string;
  }) => {
    setDepartmentIds(filters.departmentIds);
    setAgentFilter(filters.agentFilter);
    setOrganizationFilter(filters.organizationFilter);
    setDateFromFilter(filters.dateFrom);
    setDateToFilter(filters.dateTo);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number, newPageSize: number) => {
    setCurrentPage(page);
    setPageSize(newPageSize);
  };

  const handleSelectLead = (leadId: number) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead.id));
    }
  };

  // שיוך מחלקה המוני (Manager בלבד). ערך '-1' => ניקוי מחלקה
  const handleBulkAssignDepartment = async () => {
    if (!bulkAssignDepartment || selectedLeads.length === 0) return;

    try {
      const departmentId = bulkAssignDepartment === '-1' ? null : parseInt(bulkAssignDepartment);
      const result = await leadsApi.bulkAssignDepartment({
        leadIds: selectedLeads,
        departmentId
      });

      if (result.success) {
        toast.success(`${result.updatedCount} לידים שויכו למחלקה`);
        setSelectedLeads([]);
        setBulkAssignDepartment('');
        loadLeads();
      } else {
        setError(`שיוך מחלקה נכשל: ${result.errors.join(', ')}`);
      }
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedLeads.length === 0) return;

    try {
      const deleteData: BulkDeleteDto = {
        leadIds: selectedLeads
      };

      await leadsApi.bulkDelete(deleteData);
      setSelectedLeads([]);
      setShowDeleteConfirm(false);
      loadLeads();
    } catch (err) {
      setError(handleApiError(err));
    }
  };

  const handleAddLead = () => {
    setShowLeadModal(true);
  };

  // עמוד ניהול הליד — הטאב הרצוי (ברירת מחדל: פרטי ליד)
  const openLeadPage = (lead: LeadDto, tab?: 'handling' | 'schedule-tasks') => {
    navigate(tab ? `/leads/${lead.id}/manage/${tab}` : `/leads/${lead.id}/manage`);
  };

  const handleDeleteLead = (lead: LeadDto) => {
    // Check if lead has a contract
    const hasContract = !!contractStatuses[lead.id];

    setDeleteConfirmLead({
      id: lead.id,
      name: lead.fullName,
      hasContract
    });
  };

  const handleConfirmDeleteLead = async () => {
    if (!deleteConfirmLead) return;

    try {
      await leadsApi.delete(deleteConfirmLead.id);
      setDeleteConfirmLead(null);
      loadLeads();
    } catch (err) {
      setError(handleApiError(err));
      setDeleteConfirmLead(null);
    }
  };

  const handleLeadSaved = () => {
    loadLeads();
    setShowLeadModal(false);
  };

  const handleImported = () => {
    loadLeads();
  };

  // Dynamic page title based on capability
  const getPageTitle = () => {
    return isManager ? 'ניהול לידים' : 'לידים';
  };

  // Info message based on user role
  const columns = [
    // Checkbox column - only for managers
    ...(isManager ? [{
      key: 'select',
      title: 'בחירה',
      width: '60px',
      render: (_: any, record: LeadDto) => (
        <SelectCell onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedLeads.includes(record.id)}
            onChange={() => handleSelectLead(record.id)}
          />
        </SelectCell>
      )
    }] : []),
    {
      key: 'fullName',
      title: 'שם מלא',
      width: '200px'
    },
    {
      key: 'phone',
      title: 'טלפון',
      width: '150px'
    },
    {
      key: 'departmentName',
      title: 'מחלקה',
      width: '120px',
      render: (value: string) => value || '—'
    },
    {
      key: 'agentSourceName',
      title: 'סוכן',
      width: '120px',
      render: (value: string) => value || '—'
    },
    {
      key: 'organizationSourceName',
      title: 'ארגון',
      width: '120px',
      render: (value: string) => value || '—'
    },
    {
      key: 'benefitType',
      title: 'סוג קצבה',
      width: '150px',
      render: (value: number) => getBenefitTypeName(value)
    },
    {
      key: 'statusName',
      title: 'סטטוס',
      width: '150px',
      render: (value: string, record: LeadDto) => {
        // עבור ליד שחתם — אם השרת החזיר סטטוס לקוח (נציג מכירות + פרמטר מופעל),
        // מציגים את סטטוס הלקוח בדיוק כמו בטבלת הלקוחות (כולל ריבוי סטטוסים + hover)
        if (record.currentStatusName) {
          return (
            <StatusCellWithTooltip
              currentStatusName={record.currentStatusName}
              currentStatusColor={record.currentStatusColor}
              currentStages={record.currentStages}
            />
          );
        }

        const contractStatus = contractStatuses[record.id];
        const isFullySigned = contractStatus?.toLowerCase() === 'fullysigned';

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <StatusText color={record.statusColor}>
              {value}
            </StatusText>
            {isFullySigned && (
              <CheckCircle size={14} color="var(--success-color)" />
            )}
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      title: 'תאריך יצירה',
      width: '140px',
      render: (value: string) => formatDate(value)
    },
    {
      key: 'lastUpdatedAt',
      title: 'עודכן לאחרונה',
      width: '140px',
      render: (value: string) => value ? formatDate(value) : '-'
    },
    {
      key: 'actions',
      title: 'פעולות',
      width: '140px',
      render: (_: any, record: LeadDto) => (
        <ActionsCell onClick={(e) => e.stopPropagation()}>
          {isStaff && (
            <IconButton
              icon={Phone}
              onClick={(e) => { e.stopPropagation(); openLeadPage(record, 'handling'); }}
              title="טפל בליד"
              variant="primary"
            />
          )}
          <IconButton
            icon={Eye}
            onClick={(e) => { e.stopPropagation(); openLeadPage(record); }}
            title="צפייה בליד"
          />
          {record.clientId && (
            <IconButton
              icon={FolderOpen}
              onClick={(e) => { e.stopPropagation(); setClientDataLead(record); }}
              title="צפייה בנתוני לקוח"
            />
          )}
          <IconButton
            icon={Bell}
            onClick={(e) => { e.stopPropagation(); openLeadPage(record, 'schedule-tasks'); }}
            title="תזכורות"
          />
          {isManager && (
            <IconButton
              icon={Trash2}
              onClick={(e) => { e.stopPropagation(); handleDeleteLead(record); }}
              title="מחיקת ליד"
              variant="danger"
            />
          )}
        </ActionsCell>
      )
    }
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderRight>
          <PageTitle>{getPageTitle()}</PageTitle>
          <RoleIndicatorChip />
        </HeaderRight>
        <HeaderActions>
          {isStaff && (
            <Button variant="primary" onClick={handleAddLead}>
              <Plus size={16} style={{ marginLeft: '6px', verticalAlign: 'middle' }} />
              ליד חדש
            </Button>
          )}
        </HeaderActions>
      </PageHeader>

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      <FilterContainer>
        <SmartSearchBar
          value={searchTerm}
          onChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          placeholder="חיפוש לפי שם, טלפון או דוא״ל..."
        />

        <FilterRow>
          <StatusFilterTabs
            statuses={statuses}
            selectedStatuses={statusFilter}
            onChange={(values) => {
              setStatusFilter(values);
              setCurrentPage(1);
            }}
          />
          <AdvancedFilterButton onClick={() => setShowFilterDrawer(true)}>
            <Filter size={16} />
            מסננים מתקדמים
          </AdvancedFilterButton>
        </FilterRow>
      </FilterContainer>

      <FilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onApply={handleAdvancedFilter}
        onClear={handleClearFilters}
        filters={{
          departmentIds,
          agentFilter,
          organizationFilter,
          dateFrom: dateFromFilter,
          dateTo: dateToFilter
        }}
        departments={departments.filter(d => d.isActive)}
        agents={leadSources.filter(s => s.type === LeadSourceType.Agent)}
        organizations={leadSources.filter(s => s.type === LeadSourceType.Organization)}
      />

      {isManager && selectedLeads.length > 0 && (
        <BulkActions>
          <BulkInfo>
            נבחרו {selectedLeads.length} לידים
          </BulkInfo>
          <BulkButtons>
            <BulkSelect
              value={bulkAssignDepartment}
              onChange={(e) => setBulkAssignDepartment(e.target.value)}
            >
              <option value="">בחר מחלקה</option>
              <option value="-1">ללא מחלקה</option>
              {departments.filter(d => d.isActive).map(dep => (
                <option key={dep.id} value={dep.id}>
                  {dep.name}
                </option>
              ))}
            </BulkSelect>
            <BulkButton
              variant="primary"
              onClick={handleBulkAssignDepartment}
              disabled={!bulkAssignDepartment}
            >
              שייך למחלקה
            </BulkButton>
            <BulkButton
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              מחק נבחרים
            </BulkButton>
          </BulkButtons>
        </BulkActions>
      )}

      <DataTable
        data={leads}
        columns={columns}
        loading={loading}
        onRowClick={(lead: LeadDto) => openLeadPage(lead)}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          onChange: handlePageChange
        }}
        actions={
          isManager ? (
            <input
              type="checkbox"
              checked={selectedLeads.length === leads.length && leads.length > 0}
              onChange={handleSelectAll}
              title="בחר הכל"
            />
          ) : undefined
        }
      />

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title="אישור מחיקה"
        message={`האם אתה בטוח שברצונך למחוק ${selectedLeads.length} לידים? פעולה זו לא ניתנת לביטול.`}
        confirmText="מחק"
        cancelText="ביטול"
      />

      {/* Single lead delete confirmation dialog */}
      <ConfirmDialog
        isOpen={!!deleteConfirmLead}
        onClose={() => setDeleteConfirmLead(null)}
        onConfirm={handleConfirmDeleteLead}
        title="אישור מחיקת ליד"
        message={
          deleteConfirmLead?.hasContract
            ? `⚠️ אזהרה: ליד "${deleteConfirmLead.name}" כבר הפך ללקוח בטיפול.\n\nמחיקת הליד תנתק את הקישור בין הליד לתיק הלקוח, אך תיק הלקוח (כולל מסמכים, פגישות ותשלומים) ימשיך להתקיים במערכת.\nמסמכים שהועלו לליד ועדיין לא הועברו לתיק לקוח יימחקו לצמיתות.\n\nהאם אתה בטוח שברצונך להמשיך?`
            : `האם אתה בטוח שברצונך למחוק את הליד "${deleteConfirmLead?.name}"?\nמסמכים שהועלו לליד יימחקו לצמיתות.`
        }
        confirmText="מחק ליד"
        cancelText="ביטול"
        type={deleteConfirmLead?.hasContract ? "danger" : "info"}
      />

      {/* יצירת ליד חדש — צפייה ועריכה נעשות בעמוד ניהול הליד */}
      <LeadModal
        isOpen={showLeadModal}
        onClose={() => setShowLeadModal(false)}
        onSave={handleLeadSaved}
        lead={null}
        mode="create"
      />

      {isManager && (
        <ImportLeadsModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onImported={handleImported}
        />
      )}

      {clientDataLead && (
        <ClientDataViewerModal
          isOpen={!!clientDataLead}
          onClose={() => setClientDataLead(null)}
          lead={clientDataLead}
        />
      )}

    </PageContainer>
  );
};

export default LeadsPage;